import { KeyboardEvent, useEffect, useState } from 'react';
import { CLPrimaryCustomButton, CLPrimaryInput } from '../ui-controls';

import { isValidEmail } from '@/services/utils/utils.service';
import { useNhostClient, useSignInEmailPassword } from '@nhost/nextjs';
import { LoadingSpinWhiteCustom } from '../utils/loading.component';
import { useRouter } from 'next/router';

interface SignInProps {
  redirectTo?: string;
  state?: string;
  // When set, "Create an account" switches the view in place instead of navigating
  onSwitchView?: () => void;
}

export default function SignInComponent({
  redirectTo,
  state,
  onSwitchView
}: SignInProps) {
  // Variables
  const router = useRouter();
  const nhost = useNhostClient();
  const session = nhost.auth.getSession();

  // build your base sign-up URL and re-attach any redirect & state
  const signUpHref = redirectTo
    ? `/sign-up?redirect_to=${encodeURIComponent(redirectTo)}${
        state ? `&state=${state}` : ''
      }`
    : '/sign-up';

  // States
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [isEmailLoading, setEmailLoading] = useState<boolean>(false);
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

  // Hooks
  const {
    signInEmailPassword,
    needsEmailVerification,
    isLoading,
    isSuccess,
    isError,
    error
  } = useSignInEmailPassword();

  useEffect(() => {
    // Set error message
    if (isError) {
      if (error?.message) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg('Something went wrong');
      }
    } else {
      setErrorMsg('');
    }

    // Info message
    if (needsEmailVerification) {
      setSuccessMsg(
        'Please check your inbox and verify your email to proceed.'
      );
    } else {
      setSuccessMsg('');
    }

    if (isSuccess && !isLoading && state) {
      // build your full callback URL
      let target = redirectTo || '/projects';
      const params = new URLSearchParams();
      if (state) params.set('state', state);
      if (session?.accessToken) params.set('access_token', session.accessToken);
      // console.log('session', session);
      // console.log('redirectTo', redirectTo);

      const token = session?.accessToken;
      if (token) {
        localStorage.setItem('gpt_access_token', token);
      }

      // if redirectTo already has a “?”, use “&” instead of “?”
      const separator = target.includes('?') ? '&' : '?';
      target = `${target}${separator}${params.toString()}`;

      // Clean up saved redirect once it's being used
      const localStorageKey = `oauth_redirect_${state}`;
      const existing = localStorage.getItem(localStorageKey);
      if (existing) {
        localStorage.removeItem(localStorageKey);
        // console.log(`Removed ${localStorageKey} from localStorage`);
      }
      // console.log('target', target);
      router.push(target);
    }
  }, [needsEmailVerification, isLoading, isSuccess, isError, error]);

  useEffect(() => {
    // console.log('signUpHref', signUpHref);
    
    if (signUpHref && redirectTo?.includes('/oauth/callback')) {
      const stateMatch = redirectTo.match(/[?&]state=([^&]+)/);
      const state = stateMatch?.[1];
      
      if (state) {
        const existing = localStorage.getItem(`oauth_redirect_${state}`);
        if (!existing) {
          localStorage.setItem(`oauth_redirect_${state}`, redirectTo);
          // console.log(`Saved oauth_redirect_${state} to localStorage`);
        } else {
          // console.log(`oauth_redirect_${state} already exists in localStorage`);
        }
      }
    }
  }, [signUpHref]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      signIn();
    }
  };

  async function signIn() {
    // Reset
    setErrorMsg('');
    setSuccessMsg('');

    // Validation
    if (!email) {
      setErrorMsg('Please enter your email address');
      document.getElementById('sign_in_email_field')?.focus();
      return;
    } else if (!isValidEmail(email)) {
      setErrorMsg('Please enter a valid email address');
      document.getElementById('sign_in_email_field')?.focus();
      return;
    } else if (!password) {
      setErrorMsg('Please enter your password');
      document.getElementById('sign_in_password_field')?.focus();
      return;
    }

    // SignUp
    await signInEmailPassword(email, password);
  }

  return (
    <div className="mt-8">
      <div className="space-y-2">
        <div>
          <CLPrimaryInput
            id="sign_in_email_field"
            type="email"
            label="Email *"
            value={email}
            onKeyDown={handleKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(event.target.value);
            }}
          />
        </div>
        <div className="relative">
          <CLPrimaryInput
            id="sign_in_password_field"
            type={passwordVisible ? 'text' : 'password'}
            label="Password *"
            value={password}
            onKeyDown={handleKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPassword(event.target.value);
            }}
          />
          {password && (
            <button
              type="button"
              className="absolute right-3 top-[68%] transform -translate-y-1/2 text-sm text-gray-700"
              onClick={() => setPasswordVisible(!passwordVisible)}
            >
              {passwordVisible ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a10.05 10.05 0 012.504-3.592m2.882-1.892A9.955 9.955 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.954 9.954 0 01-4.032 5.354M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3l18 18"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
        <div className="flex justify-end">
          <a href="forget-password">Forgot password?</a>
        </div>
      </div>

      {/* Turnstile container */}
      <div id="cl-turnstile-container"></div>

      {/* Error message */}
      {errorMsg && <div className="mt-2 text-red-400">{errorMsg}</div>}

      {/* Success message */}
      {successMsg && <div className="mt-2 text-black">{successMsg}</div>}

      <div className="mt-4 w-full">
        <CLPrimaryCustomButton
          className="w-full text-base px-3 py-3"
          onClick={() => {
            signIn();
          }}
          disabled={isEmailLoading || isLoading}
        >
          {(isEmailLoading || isLoading) && (
            <LoadingSpinWhiteCustom className="w-5 h-5"></LoadingSpinWhiteCustom>
          )}
          Sign in
        </CLPrimaryCustomButton>
      </div>

      {/* Go to signUp screen */}
      <div className="flex items-center justify-center mt-4">
        {/* Already have an account? */}
        <span className="text-black/[0.7]">Don&apos;t have an account?</span>
        {/* Add extra space here */}
        <span className="mx-1"></span>
        <a
          href="sign-up"
          onClick={event => {
            if (onSwitchView) {
              event.preventDefault();
              onSwitchView();
            }
          }}
        >
          Create an account.
        </a>
      </div>
    </div>
  );
}
